import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScheduleConfig } from '../../../hooks/useScheduleConfig';
import { useNextRunTimes } from '../../../hooks/useNextRunTimes';
import { useUI } from '../../../contexts/UIContext';
import {
  ClockIcon,
  Cog6ToothIcon,
  PlayIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { formatNextRunTime, getNextRunStatusColor } from '../../../utils/scheduleUtils';

// Format 24-hour time to 12-hour local time
const formatTime12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const ValidationAutomationControls = ({ connectionId }) => {
  const { schedule, loading: scheduleLoading, updateSchedule } = useScheduleConfig(connectionId);
  const { showNotification } = useUI();
  const [showConfig, setShowConfig] = useState(false);
  const [localConfig, setLocalConfig] = useState(null);

  const {
    nextRuns,
    loading: nextRunsLoading,
    refresh: refreshNextRuns,
    triggerManualRun,
    manualRunLoading
  } = useNextRunTimes(connectionId, {
    refreshInterval: 60000,
    enabled: !!connectionId
  });

  // Sync local config when schedule loads
  React.useEffect(() => {
    if (schedule?.validation_automation) {
      setLocalConfig(schedule.validation_automation);
    }
  }, [schedule]);

  const validationAutomationEnabled = schedule?.validation_automation?.enabled || false;
  const validationNextRun = nextRuns?.validation_automation;

  const handleToggleEnabled = async () => {
    if (!schedule) return;

    const newEnabled = !validationAutomationEnabled;
    const updatedSchedule = {
      ...schedule,
      validation_automation: {
        ...schedule.validation_automation,
        enabled: newEnabled,
        // Set defaults if enabling for first time
        schedule_type: schedule.validation_automation?.schedule_type || 'daily',
        time: schedule.validation_automation?.time || '06:00',
        timezone: schedule.validation_automation?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    const success = await updateSchedule(updatedSchedule);
    if (success) {
      showNotification(
        newEnabled ? 'Validation automation enabled' : 'Validation automation disabled',
        'success'
      );
      setTimeout(() => refreshNextRuns(), 1000);
    }
  };

  const handleSaveConfig = async () => {
    if (!schedule || !localConfig) return;

    const updatedSchedule = {
      ...schedule,
      validation_automation: localConfig
    };

    const success = await updateSchedule(updatedSchedule);
    if (success) {
      showNotification('Validation schedule updated', 'success');
      setShowConfig(false);
      setTimeout(() => refreshNextRuns(), 1000);
    }
  };

  const handleTriggerManualRun = async () => {
    try {
      showNotification('Starting validation run...', 'info');
      const success = await triggerManualRun('validation_automation');
      if (success) {
        showNotification('Validation run started!', 'success');
        setTimeout(() => refreshNextRuns(), 2000);
      } else {
        showNotification('Failed to start validation run', 'error');
      }
    } catch (error) {
      showNotification('Error starting validation run', 'error');
    }
  };

  if (scheduleLoading) {
    return (
      <div className="bg-white border border-secondary-200 rounded-lg p-4">
        <div className="animate-pulse h-20 bg-secondary-100 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-lg">
      <div className="px-4 py-3 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-secondary-900 flex items-center">
            <ClockIcon className="h-4 w-4 mr-2" />
            Validation Automation
          </h4>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-secondary-700">
              Scheduled Validation Runs
            </span>
            <p className="text-xs text-secondary-500 mt-0.5">
              Run all validation rules on a schedule (auto-cached rules also run during profiling)
            </p>
          </div>
          <button
            onClick={handleToggleEnabled}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              validationAutomationEnabled ? 'bg-primary-600' : 'bg-secondary-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                validationAutomationEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Schedule Config (when enabled) */}
        {validationAutomationEnabled && (
          <>
            {/* Current Schedule Summary */}
            <div className="bg-secondary-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-secondary-700">
                  <span className="font-medium">Schedule: </span>
                  {schedule?.validation_automation?.schedule_type === 'weekly'
                    ? `Weekly at ${formatTime12Hour(schedule?.validation_automation?.time || '06:00')}`
                    : `Daily at ${formatTime12Hour(schedule?.validation_automation?.time || '06:00')}`
                  }
                </div>
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center"
                >
                  {showConfig ? (
                    <>
                      <ChevronUpIcon className="h-3 w-3 mr-1" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Cog6ToothIcon className="h-3 w-3 mr-1" />
                      Edit
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Expandable Config */}
            {showConfig && localConfig && (
              <div className="border border-secondary-200 rounded-lg p-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Frequency
                  </label>
                  <select
                    value={localConfig.schedule_type || 'daily'}
                    onChange={(e) => setLocalConfig({ ...localConfig, schedule_type: e.target.value })}
                    className="block w-full text-sm border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Time
                  </label>
                  <input
                    type="time"
                    value={localConfig.time || '06:00'}
                    onChange={(e) => setLocalConfig({ ...localConfig, time: e.target.value })}
                    className="block w-full text-sm border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {localConfig.schedule_type === 'weekly' && (
                  <div>
                    <label className="block text-xs font-medium text-secondary-700 mb-1">
                      Days
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <button
                          key={day}
                          onClick={() => {
                            const days = localConfig.days || [];
                            const newDays = days.includes(day)
                              ? days.filter(d => d !== day)
                              : [...days, day];
                            setLocalConfig({ ...localConfig, days: newDays });
                          }}
                          className={`px-2 py-1 text-xs rounded ${
                            (localConfig.days || []).includes(day)
                              ? 'bg-primary-100 text-primary-800'
                              : 'bg-secondary-100 text-secondary-600'
                          }`}
                        >
                          {day.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleSaveConfig}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Save Schedule
                </button>
              </div>
            )}

            {/* Next Run Info */}
            {validationNextRun && !nextRunsLoading && (
              <div className={`p-3 rounded-lg text-sm ${
                validationNextRun.is_overdue
                  ? 'bg-danger-50 border border-danger-200'
                  : validationNextRun.currently_running
                  ? 'bg-primary-50 border border-primary-200'
                  : 'bg-accent-50 border border-accent-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {validationNextRun.currently_running ? (
                      <LoadingSpinner size="xs" className="mr-2" />
                    ) : (
                      <CalendarIcon className={`h-4 w-4 mr-2 ${getNextRunStatusColor(validationNextRun)}`} />
                    )}
                    <span className={getNextRunStatusColor(validationNextRun)}>
                      {validationNextRun.currently_running
                        ? 'Running now...'
                        : validationNextRun.is_overdue
                        ? 'Overdue'
                        : formatNextRunTime(validationNextRun)
                      }
                    </span>
                  </div>
                  {!validationNextRun.currently_running && (
                    <button
                      onClick={handleTriggerManualRun}
                      disabled={manualRunLoading}
                      className="flex items-center px-2 py-1 text-xs font-medium bg-white border border-secondary-200 rounded hover:bg-secondary-50 disabled:opacity-50"
                    >
                      <PlayIcon className="h-3 w-3 mr-1" />
                      Run Now
                    </button>
                  )}
                </div>
              </div>
            )}

            {nextRunsLoading && (
              <div className="flex items-center text-xs text-secondary-500">
                <LoadingSpinner size="xs" className="mr-2" />
                Loading schedule...
              </div>
            )}
          </>
        )}

        {/* Issue Creation Settings Link */}
        <div className="border-t pt-3">
          <Link
            to={`/settings/validation-issues?connection=${connectionId}`}
            className="flex items-center justify-between text-sm text-secondary-600 hover:text-secondary-800"
          >
            <span>Issue Creation Settings</span>
            <Cog6ToothIcon className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ValidationAutomationControls;
